// src/pages/Distributors.jsx
import { Box, Button, HStack, Spacer, Separator, Spinner, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, UI } from "components";
import { API } from "config";
import { toaster } from "config";
import useCart from "hooks/useCart";
import useDistributors from "hooks/useDistributors";
import { useState } from "react";
import { useNavigate } from "react-router";

export default function Distributors() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const cart = useCart();
    const [page, setPage] = useState(1);

    // Campos separados para nome, cidade, estado e movimentação financeira
    const [name, setName] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [financial, setFinancial] = useState('bigger');

    const [loading, setLoading] = useState(false);

    // Passa os filtros para o hook
    const distributors = useDistributors(page, { name, city, state, financial });

    const handleChange = async (distributor) => {
        if (!distributor) return;
        if (!window.confirm("Tem certeza que deseja alterar o núcleo do pedido?")) return;
        setLoading(distributor?.id);

        try {
            await API.put("/store/cart/distributor", { distributor_id: distributor?.id });
            queryClient.invalidateQueries("products");
            cart.refetch();
            toaster.create({ title: "O núcleo do pedido foi alterado.", type: "success" });
            navigate("/loja");
        } catch (err) {
            toaster.create({ title: "Não foi possível alterar o núcleo agora. Tente novamente", type: "warning" });
            setLoading(false);
        }
    };

    // Função para limpar filtros e campos visuais
    const clearFilters = () => {
        setName("");
        setCity("");
        setState("");
        setFinancial('');
        setPage(1);
    };

    return (
        <>
            <UI.Header title="Núcleos Bonífica" backPath="/loja" />

            <HStack spacing={3} px={4} mb={4} flexWrap="wrap" alignItems="center">
                <UI.Searchable
                    placeholder="Nome..."
                    onChange={setName}
                    value={name}
                    minLength={1}
                />
                <UI.Searchable
                    placeholder="Cidade..."
                    onChange={setCity}
                    value={city}
                    minLength={1}
                />
                <UI.Searchable
                    placeholder="Estado (UF)..."
                    onChange={setState}
                    value={state}
                    minLength={2}
                    maxWidth="100px"
                />
                <Spacer />
                <Button size="sm" colorScheme="orange" onClick={clearFilters} whiteSpace="nowrap">
                    Limpar filtros
                </Button>
            </HStack>

            <Separator my={6} />

            <UI.Show visible={distributors.isLoading}>
                <UI.Alert indicator={<Spinner />} children="Carregando núcleos..." />
            </UI.Show>

            {distributors.data?.data?.map((item) => (
                <Dist
                    key={item?.id}
                    data={item}
                    loading={loading}
                    cart={cart?.data}
                    handleChange={handleChange}
                />
            ))}

            <UI.Show visible={!distributors.isLoading}>
                <Pagination distributors={distributors} setPage={setPage} />
            </UI.Show>
        </>
    );
}

const Dist = ({ data, loading, cart, handleChange }) => {
    const is_selected = data?.id === cart?.distributor_from?.id;

    return (
        <Box
            py={2}
            px={4}
            mb={4}
            borderWidth={1}
            borderRadius={4}
            borderColor={is_selected ? "orange.fg" : null}
        >
            <HStack>
                <Button
                    onClick={() => handleChange(data)}
                    disabled={is_selected || !!loading}
                    loading={loading === data?.id}
                    colorPalette="orange"
                    size="xs"
                    children={is_selected ? "Selecionado" : "Selecionar"}
                />
                <Text fontSize="sm" fontWeight="bold" children={data?.name} />
            </HStack>
        </Box>
    );
};

const Pagination = ({ distributors, setPage }) => {
    return (
        <UI.Pagination.PaginationRoot
            mt={4}
            count={distributors.data?.meta?.last_page}
            size="xs"
            pageSize={1}
            page={distributors.data?.meta?.current_page}
            defaultPage={1}
            onPageChange={(e) => setPage(e.page)}
        >
            <HStack>
                <UI.Pagination.PaginationPrevTrigger />
                <UI.Pagination.PaginationItems disabled={distributors.isFetching} />
                <UI.Pagination.PaginationNextTrigger />
            </HStack>
        </UI.Pagination.PaginationRoot>
    );
};
